<script setup lang="ts">
/**
 * The editing control for one dynamic block property, picked from what the property
 * accepts: a switch for a 0/1 flip, a segmented pair for two named states, a select
 * for longer lists, a numeric field when the value is free.
 *
 * Values are handed over as the strings ODA expects; only the angle conversion is
 * done here, because radians are an ODA storage detail, not something to show.
 */
import { computed, ref } from "vue";
// From the pure classification module, not blocks/dynamic: a UI component has no
// business pulling in the ODA engine just to read a type.
import {
  UNITS_ANGULAR,
  type DynBlockPropertyInfo,
} from "@/drawing/blocks/dynamicGrouping";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DynBlockAnglePicker from "../DynBlockAnglePicker.vue";
import CanvasSelect from "../CanvasSelect.vue";

const props = defineProps<{
  property: DynBlockPropertyInfo;
  /** Narrower layout, for the two coordinates sharing a point parameter's line. */
  compact?: boolean;
}>();

const emit = defineEmits<{
  /** A discrete edit: apply it and record an undo step. */
  change: [value: string];
  /** Continuous edit in progress: apply it without recording anything. */
  preview: [value: string];
  /** End of a continuous edit: record the whole gesture as one step. */
  commit: [oldValue: string, newValue: string];
}>();

/**
 * A property whose only values are 0 and 1 is a boolean in disguise (AutoCAD flip
 * states) and deserves a switch rather than a list of two cryptic numbers.
 */
const isBoolean = computed(
  () =>
    props.property.allowedValues.length === 2 &&
    props.property.allowedValues.every(
      (value) => value === "0" || value === "1",
    ),
);

/** ODA keeps angles in radians; users think in degrees. */
const isAngle = computed(() => props.property.unitsType === UNITS_ANGULAR);

/** Value as shown in a numeric field: degrees for angles, raw otherwise. */
const displayNumber = computed(() => {
  const raw = Number(props.property.value);
  if (!Number.isFinite(raw)) return props.property.value;
  const shown = isAngle.value ? (raw * 180) / Math.PI : raw;
  // Trim floating point noise without losing precision on small distances.
  return String(Number(shown.toFixed(4)));
});

function commitNumber(input: string): void {
  const typed = Number(input);
  if (!Number.isFinite(typed)) return;
  emit("change", String(isAngle.value ? (typed * Math.PI) / 180 : typed));
}

function toggleBoolean(): void {
  emit("change", props.property.value === "1" ? "0" : "1");
}

// ==================== Angle dial ====================

const anglePickerAnchor = ref<{ x: number; y: number } | null>(null);
const angleAtOpen = ref("0");
const dialButton = ref<HTMLElement | null>(null);

function toRadiansString(degrees: number): string {
  return String((degrees * Math.PI) / 180);
}

function openAnglePicker(): void {
  const rect = dialButton.value?.getBoundingClientRect();
  if (!rect) return;
  angleAtOpen.value = props.property.value;
  anglePickerAnchor.value = { x: rect.right, y: rect.top + rect.height / 2 };
}
</script>

<template>
  <!-- Boolean (flip state): a switch reads better than "0" / "1". -->
  <button
    v-if="isBoolean"
    type="button"
    class="switch"
    :class="{ on: property.value === '1' }"
    role="switch"
    :aria-checked="property.value === '1'"
    @click="toggleBoolean"
  >
    <span class="switch-knob"></span>
  </button>

  <!-- Two named states: both fit side by side. -->
  <div v-else-if="property.allowedValues.length === 2" class="segmented">
    <button
      v-for="value in property.allowedValues"
      :key="value"
      type="button"
      class="segment"
      :class="{ active: value === property.value }"
      @click="emit('change', value)"
    >
      {{ value }}
    </button>
  </div>

  <!-- Free numeric scalar (linear distance, rotation angle, point coordinate). -->
  <div v-else-if="!property.allowedValues.length" class="field-number">
    <input
      type="number"
      inputmode="decimal"
      enterkeyhint="done"
      :value="displayNumber"
      step="any"
      :class="{ compact }"
      @change="(e) => commitNumber((e.target as HTMLInputElement).value)"
      @keyup.enter="
        (e) => {
          commitNumber((e.target as HTMLInputElement).value);
          (e.target as HTMLInputElement).blur();
        }
      "
    />
    <span v-if="isAngle" class="field-unit">°</span>

    <!-- Turning a block to the right angle is done by eye, not by typing degrees. -->
    <button
      v-if="isAngle"
      ref="dialButton"
      type="button"
      class="dial-button"
      :title="$t('canvas:floating.dynblock.angleTool')"
      @click="openAnglePicker"
    >
      <PhosphorIcon name="angle" :size="15" />
    </button>

    <DynBlockAnglePicker
      v-if="anglePickerAnchor"
      :degrees="Number(displayNumber)"
      :label="property.name"
      :anchor="anglePickerAnchor"
      @preview="(degrees) => emit('preview', toRadiansString(degrees))"
      @commit="
        (degrees) => emit('commit', angleAtOpen, toRadiansString(degrees))
      "
      @close="anglePickerAnchor = null"
    />
  </div>

  <!-- Three or more: a dropdown keeps the row compact. -->
  <CanvasSelect
    v-else
    class="field-select"
    :model-value="property.value"
    :options="property.allowedValues"
    :placeholder="$t('canvas:floating.dynblock.unknownValue')"
    @update:model-value="(value) => emit('change', value)"
  />
</template>

<style lang="scss" scoped>
.field-select {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 60%;
}

.field-number {
  display: flex;
  align-items: center;
  gap: 3px;
  flex: 0 0 auto;

  input {
    width: 80px;
    padding: 3px 6px;
    border-radius: 4px;
    border: 1px solid var(--canvas-border);
    background-color: rgba(255, 255, 255, 0.06);
    color: white;
    font-size: 0.8rem;

    &.compact {
      width: 56px;
    }

    &:focus {
      outline: none;
      border-color: var(--canvas-btn-active-bg);
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
}

.field-unit {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

.dial-button {
  display: inline-flex;
  padding: 3px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
}

.segmented {
  display: inline-flex;
  border: 1px solid var(--canvas-border);
  border-radius: 4px;
  overflow: hidden;
}

.segment {
  padding: 3px 8px;
  border: 0;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.78rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
  }

  &.active {
    background-color: var(--canvas-btn-active-bg);
    color: white;
  }
}

.switch {
  flex: 0 0 auto;
  width: 38px;
  height: 20px;
  padding: 2px;
  border: 1px solid var(--canvas-border);
  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.08);
  transition: background-color 0.15s ease;

  &.on {
    background-color: var(--bs-primary, #1e5aa8);
  }
}

.switch-knob {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: white;
  transition: transform 0.15s ease;

  .switch.on & {
    transform: translateX(18px);
  }
}
</style>
